import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import { gtmEvent } from 'lib/gtm'
import {
  PROMO_CALLOUT_BORDER_RADIUS,
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
} from 'lib/form-field-styles'
import CloseIcon from 'components/material-symbol/icons/Close'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import FileCopyOutlinedIcon from 'components/material-symbol/icons/FileCopy'
import AutoAwesomeIcon from 'components/material-symbol/icons/AutoAwesome'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import CreateFromTemplateDialog from 'components/group-events/create-group-event-form-wizard/dialogs/create-from-template-dialog'
import CopyExistingEventDialog from 'components/group-events/create-group-event-form-wizard/dialogs/copy-existing-event-dialog'
import { GET_GROUP_EVENT_TEMPLATES } from 'components/group-events/form-option-hooks'

const START_OPTIONS = {
  TEMPLATE: 'template',
  SCRATCH: 'scratch',
  COPY: 'copy',
}

const useStyles = makeStyles(theme => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      alignItems: 'flex-start',
      overflowY: 'auto',
    },
  },
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: 1040,
    backgroundColor: theme.palette.common.white,
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    padding: theme.spacing(4),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    color: theme.palette.text.primary,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    paddingRight: theme.spacing(7),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardsRowTwoUp: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
  },
  card: {
    width: '100%',
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2.5, 2.5),
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    border: '1px solid #D5DDE3',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#9AA8B3',
    },
  },
  cardTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#00234B',
    textAlign: 'center',
    textTransform: 'none',
  },
  cardDescription: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  freeTierNote: {
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
}))

const StartOptionCard = ({ icon, title, description, onClick, classes }) => (
  <Box className={classes.cardColumn}>
    <Box
      className={classes.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
    >
      <CircularHighlightedIcon
        icon={icon}
        size={56}
        iconSize={28}
        highlightColor={CALLOUT_BACKGROUND}
        iconColor={CALLOUT_FOREGROUND}
      />
      <Typography className={classes.cardTitle}>{title}</Typography>
      <Typography className={classes.cardDescription}>{description}</Typography>
    </Box>
  </Box>
)

const CreateEventQuickStartStep = ({
  canBuildOwnEvent,
  onContinue,
  onCancel,
}) => {
  const classes = useStyles()
  const { register } = useFormContext()
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)

  useEffect(() => {
    register('templateId')
  }, [register])

  useQuery(GET_GROUP_EVENT_TEMPLATES, {
    fetchPolicy: 'cache-first',
  })

  const handleAfterSelect = () => {
    setTemplateDialogOpen(false)
    setCopyDialogOpen(false)
    onContinue()
  }

  const trackOptionAction = formAction => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'group-event-creation',
      formAction,
    })
  }

  const handleTemplateClick = () => {
    trackOptionAction('start-from-template')
    setTemplateDialogOpen(true)
  }

  const handleScratchClick = () => {
    trackOptionAction('start-from-scratch')
    onContinue()
  }

  const handleCopyClick = () => {
    trackOptionAction('copy-from-existing')
    setCopyDialogOpen(true)
  }

  const cardOptions = [
    {
      id: START_OPTIONS.TEMPLATE,
      icon: DescriptionIcon,
      title: 'Start from Template',
      description: 'Prefilled with PGA best-practice defaults.',
      onClick: handleTemplateClick,
    },
    ...(canBuildOwnEvent
      ? [
        {
          id: START_OPTIONS.SCRATCH,
          icon: AutoAwesomeIcon,
          title: 'Start from Scratch',
          description: 'A blank event you build up field by field.',
          onClick: handleScratchClick,
        },
      ]
      : []),
    {
      id: START_OPTIONS.COPY,
      icon: FileCopyOutlinedIcon,
      title: 'Duplicate Event',
      description: 'Duplicate a past or upcoming event.',
      onClick: handleCopyClick,
    },
  ]

  return (
    <>
      {!templateDialogOpen && !copyDialogOpen ? (
        <Box className={classes.overlay}>
          <Box className={classes.modal}>
            <IconButton
              className={classes.closeButton}
              aria-label="Close"
              onClick={onCancel}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              className={classes.title}
              data-cy="get-started-heading"
              component="h1"
            >
              How do you want to start?
            </Typography>
            <Typography className={classes.subtitle}>
              Pick a starting point. You can customize everything after.
            </Typography>

            {!canBuildOwnEvent ? (
              <Typography className={classes.freeTierNote}>
                You have reached your group event limit on the starter tier.
                Template and copy options are still available.
              </Typography>
            ) : null}

            <Box
              className={clsx(
                classes.cardsRow,
                !canBuildOwnEvent && classes.cardsRowTwoUp,
              )}
            >
              {cardOptions.map(option => (
                <StartOptionCard
                  key={option.id}
                  {...option}
                  classes={classes}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}

      <CreateFromTemplateDialog
        isOpen={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onAfterSelect={handleAfterSelect}
      />
      <CopyExistingEventDialog
        isOpen={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        isFreeTierReached={!canBuildOwnEvent}
        onAfterSelect={handleAfterSelect}
      />
    </>
  )
}

export default CreateEventQuickStartStep
